/**
 * runCaptionFanOut — pure, dependency-injected control flow for the
 * one-shot "Rewrite for each platform" action (Task 16).
 *
 * Extracted the same way `run-caption-edit.ts` extracted the single-platform
 * rewrite closure — so the skip/partial-failure contract can be unit-tested
 * without mounting PostBody.
 *
 * Contract (spec: task-16-brief.md):
 *  - Fires `rewriteCaption()` once per attached platform, in parallel,
 *    EXCEPT any platform `hasPlatformOverride` already reports as
 *    customized — those are SKIPPED, never overwritten (no N-way undo
 *    exists for a silent overwrite).
 *  - Every successful platform's result is written to that platform's own
 *    `platform_captions[platform]` override in ONE merged write (via the
 *    injected `saveOverrides`) — the same end state a manual per-platform
 *    edit already produces today, just batched so N parallel rewrite calls
 *    can't race each other over the same JSON column. The merge base is
 *    re-read (`getPlatformCaptions()`) as late as possible — right before
 *    the write, not at call-start — so a commit that lands on a DIFFERENT
 *    platform tab during the N parallel network calls isn't clobbered by a
 *    merge against a stale snapshot (review finding, Minor).
 *  - The result is ALWAYS named in a toast, never silently partial: what
 *    rewrote, what was skipped (and why), what failed (and why — the actual
 *    rewrite error is threaded through `friendlyMessageWithRetry`, the same
 *    helper the single-platform rewrite path uses, so a 429 mid-fan-out
 *    names itself instead of a generic "failed"). A silent skip is this
 *    codebase's most common bug class — see `buildFanOutResultMessage`.
 *  - A CAS conflict on the merged save (someone/something else saved this
 *    post first — very plausible here: editing the master caption then
 *    immediately firing the fan-out blurs the textarea and commits a save,
 *    bumping `updated_at` out from under the in-flight `expectedUpdatedAt`)
 *    is reported distinctly from a generic save failure: "try again" is
 *    useless advice for a conflict (retrying replays the exact same stale
 *    token) — the honest message asks for a reload instead.
 */

import { captionPlatformRewriteInstruction, toRewritePlatform } from './caption-writing-tools';
import { type PlatformCaptions, hasPlatformOverride } from './platform-caption';

import { friendlyMessageWithRetry } from '@/lib/errors/friendly-errors';
import type { RewritePlatform } from '@/services/media/caption-rewrite';

/**
 * Outcome of the merged `platform_captions` write. `'conflict'` and
 * `'locked'` are reported distinctly from a generic `'unknown'` failure
 * because each needs different user-facing advice (reload vs. nothing to do
 * here, the caller already surfaced a lock-specific toast vs. try again).
 */
export type FanOutSaveOutcome =
  { ok: true } | { ok: false; reason: 'conflict' | 'locked' | 'unknown' };

export interface RunCaptionFanOutDeps {
  /** The shared master caption text to rewrite from (draft if dirty, else the resolved source). */
  masterCaption: string;
  /** Every platform this fan-out should target (already narrowed to the send-eligible set — a paused platform never publishes, so rewriting it would waste a paid AI call for nothing usable). */
  platforms: string[];
  /**
   * Reads the post's CURRENT `platform_captions` map. A function, not a
   * value — called once up-front to partition targets/skips, and again
   * right before the merged write so the write's base reflects whatever
   * landed on the ref while the N parallel rewrite calls were in flight.
   */
  getPlatformCaptions: () => PlatformCaptions;
  /** The post's shared `content` — `hasPlatformOverride` needs it to tell "customized" from "identical to master". */
  sharedContent: string | null | undefined;
  /** Supabase access token — required by the rewrite service. */
  accessToken?: string;
  /** Display label for a platform id, e.g. "instagram" -> "Instagram". */
  platformLabel: (platformId: string) => string;
  /** Injected rewrite service — makes the fn testable without network. */
  rewriteCaption: (args: {
    original: string;
    instruction: string;
    accessToken: string;
    platform?: RewritePlatform;
  }) => Promise<string>;
  /** Persists the FULL next `platform_captions` map in one write. */
  saveOverrides: (nextPlatformCaptions: PlatformCaptions) => Promise<FanOutSaveOutcome>;
  toast: {
    success: (msg: string) => void;
    error: (msg: string) => void;
    /** Neutral outcome — e.g. every platform was already customized, nothing to do. */
    info: (msg: string) => void;
  };
}

function joinLabels(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/**
 * Builds the honest, fully-named toast copy for a fan-out result. Never
 * collapses to a generic "Done" — every platform that rewrote, was
 * skipped, or failed is named explicitly (spec example: "Rewrote Instagram
 * and TikTok. Skipped LinkedIn — it has a custom caption."). When platforms
 * failed, `failureReason` (a `friendlyMessageWithRetry` string) names the
 * actual cause instead of a bare "failed" — this is the fan-out's own
 * failure path, not a generic one, so a 429 across N parallel calls
 * surfaces the real retry hint.
 */
export function buildFanOutResultMessage(args: {
  succeeded: string[];
  skipped: string[];
  failed: string[];
  failureReason?: string;
  platformLabel: (platformId: string) => string;
}): string {
  const { succeeded, skipped, failed, failureReason, platformLabel } = args;
  const parts: string[] = [];
  if (succeeded.length > 0) {
    parts.push(`Rewrote ${joinLabels(succeeded.map(platformLabel))}.`);
  }
  if (skipped.length > 0) {
    const pronoun = skipped.length === 1 ? 'it has' : 'they have';
    parts.push(`Skipped ${joinLabels(skipped.map(platformLabel))} — ${pronoun} a custom caption.`);
  }
  if (failed.length > 0) {
    // Strip a trailing period from the injected reason (friendlyMessageWithRetry
    // strings are already full sentences) so the clause doesn't end in "..".
    const trimmedReason = failureReason?.replace(/\.+$/, '');
    const reasonSuffix = trimmedReason ? ` — ${trimmedReason}` : '';
    parts.push(`Failed to rewrite ${joinLabels(failed.map(platformLabel))}${reasonSuffix}.`);
  }
  return parts.length > 0 ? parts.join(' ') : 'Nothing to rewrite.';
}

/**
 * Runs the fan-out control flow:
 *  1. Guards on an empty master caption / missing auth.
 *  2. Partitions target platforms into ones with no override vs. skipped
 *     (already customized) using `hasPlatformOverride`.
 *  3. If every platform is skipped, reports the no-op and returns — no
 *     network calls fired.
 *  4. Fires `rewriteCaption` for every target IN PARALLEL.
 *  5. Merges every SUCCESSFUL result into the platform_captions map — read
 *     fresh via `getPlatformCaptions()` right before the write — and
 *     persists it in ONE `saveOverrides` write.
 *  6. Reports a CAS conflict or a lock distinctly from a generic save
 *     failure (see module doc); always reports the full rewrite outcome via
 *     `buildFanOutResultMessage`, with the real per-failure cause threaded
 *     through `friendlyMessageWithRetry`.
 */
export async function runCaptionFanOut(deps: RunCaptionFanOutDeps): Promise<void> {
  const {
    masterCaption,
    platforms,
    getPlatformCaptions,
    sharedContent,
    accessToken,
    platformLabel,
    rewriteCaption,
    saveOverrides,
    toast,
  } = deps;

  if (!masterCaption.trim()) {
    toast.error('Add a caption to rewrite first');
    return;
  }
  if (!accessToken) {
    toast.error('Sign in required');
    return;
  }

  const initialCaptions = getPlatformCaptions();
  const targets: string[] = [];
  const skipped: string[] = [];
  for (const platformId of platforms) {
    if (hasPlatformOverride(sharedContent, initialCaptions, platformId)) {
      skipped.push(platformId);
    } else {
      targets.push(platformId);
    }
  }

  if (targets.length === 0) {
    toast.info(buildFanOutResultMessage({ succeeded: [], skipped, failed: [], platformLabel }));
    return;
  }

  const results = await Promise.allSettled(
    targets.map((platformId) =>
      rewriteCaption({
        original: masterCaption,
        instruction: captionPlatformRewriteInstruction(platformLabel(platformId)),
        accessToken,
        platform: toRewritePlatform(platformId),
      }),
    ),
  );

  const succeeded: string[] = [];
  const failed: string[] = [];
  const rewritten: PlatformCaptions = {};
  let firstFailureReason: unknown;
  results.forEach((result, index) => {
    const platformId = targets[index];
    if (result.status === 'fulfilled') {
      succeeded.push(platformId);
      rewritten[platformId] = result.value;
    } else {
      failed.push(platformId);
      if (firstFailureReason === undefined) firstFailureReason = result.reason;
    }
  });

  if (succeeded.length > 0) {
    // Re-read the merge base as late as possible: the N parallel calls above
    // just took real wall-clock time, and something else (a manual edit on a
    // different tab, a retried save) may have landed on the ref meanwhile.
    // Merging against the snapshot captured before those calls even started
    // would silently drop that other commit.
    const nextCaptions: PlatformCaptions = { ...getPlatformCaptions(), ...rewritten };
    const saveOutcome = await saveOverrides(nextCaptions);
    if (saveOutcome.ok === false) {
      if (saveOutcome.reason === 'conflict') {
        toast.error(
          'This post was edited elsewhere — reload the post, then run the rewrite again.',
        );
      } else if (saveOutcome.reason !== 'locked') {
        // 'locked' is already surfaced by the caller's lock-specific toast
        // (showLockedEditToast) — a second generic toast here would just be
        // redundant, not additive.
        toast.error('Rewrote captions but failed to save them — try again.');
      }
      return;
    }
  }

  const failureReason =
    failed.length > 0 ? friendlyMessageWithRetry(firstFailureReason) : undefined;
  const message = buildFanOutResultMessage({
    succeeded,
    skipped,
    failed,
    failureReason,
    platformLabel,
  });
  if (failed.length > 0) toast.error(message);
  else if (succeeded.length > 0) toast.success(message);
  else toast.info(message);
}
